import * as authApi from './auth.api';
import { 
  Turma, Aluno, Prova, Question, Correcao, 
  LessonPlan, LessonPlanGenerationRequest 
} from '@provaplus/shared';

const RAW_API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
const API_BASE_URL = RAW_API_URL.endsWith('/api')
  ? RAW_API_URL
  : `${RAW_API_URL.replace(/\/+$/, '')}/api`;

async function getAuthToken(): Promise<string | null> {
  return authApi.getStoredToken();
}

export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getAuthToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache',
    ...(options.headers as Record<string, string> || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
    cache: 'no-store',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Erro na requisição: ${response.status}`);
  }

  return response.json();
}

// ==================== AUTH ====================
export async function signIn(email: string, password: string) {
  return authApi.login({ email, password });
}

export async function signUp(data: { email: string; password: string; name: string }) {
  return authApi.register(data);
}

export function signOut() {
  authApi.logout();
}

export async function getProfile() {
  return authApi.getProfile();
}

export async function updateProfile(data: any) {
  return apiRequest<any>('/auth/profile', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function changePassword(newPassword: string) {
  return apiRequest<any>('/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ newPassword }),
  });
}

export async function resetPasswordRequest(email: string) {
  return apiRequest<any>('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function refreshSession(refreshToken: string) {
  return apiRequest<any>('/auth/refresh', {
    method: 'POST',
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
}

// ==================== TURMAS ====================
export async function getTurmas() {
  return apiRequest<Turma[]>('/turmas');
}

export async function getTurmaById(id: string) {
  return apiRequest<Turma>(`/turmas/${id}`);
}

export async function createTurma(turma: Partial<Turma>) {
  return apiRequest<Turma>('/turmas', {
    method: 'POST',
    body: JSON.stringify(turma),
  });
}

export async function updateTurma(id: string, updates: Partial<Turma>) {
  return apiRequest<Turma>(`/turmas/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

export async function deleteTurma(id: string) {
  return apiRequest<{ success: boolean }>(`/turmas/${id}`, { method: 'DELETE' });
}

export async function duplicateTurma(id: string) {
  return apiRequest<Turma>(`/turmas/${id}/duplicate`, { method: 'POST' });
}

// ==================== ALUNOS ====================
export async function getAlunos(turmaId: string) {
  return apiRequest<Aluno[]>(`/alunos/turma/${turmaId}`);
}

export async function getAlunosCount(turmaId: string): Promise<{ count: number }> {
  return apiRequest<{ count: number }>(`/alunos/turma/${turmaId}/count`);
}

export async function createAluno(aluno: Partial<Aluno>) {
  return apiRequest<Aluno>('/alunos', {
    method: 'POST',
    body: JSON.stringify(aluno),
  });
}

export async function createAlunosBatch(turmaId: string, alunosData: {name: string, sequence_number?: number}[]) {
  const alunos = alunosData.map((item) => ({ 
    turma_id: turmaId, 
    name: item.name,
    sequence_number: item.sequence_number 
  }));
  return apiRequest<Aluno[]>('/alunos/batch', {
    method: 'POST',
    body: JSON.stringify({ alunos }),
  });
}

export async function updateAluno(id: string, updates: Partial<Aluno>) {
  return apiRequest<Aluno>(`/alunos/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

export async function deleteAluno(id: string) {
  return apiRequest<{ success: boolean }>(`/alunos/${id}`, { method: 'DELETE' });
}

// ==================== ALUNO PUBLIC ROUTES ====================
export async function getPublicTurmaInfo(turmaId: string) {
  return apiRequest<any>(`/turmas/public/${turmaId}`);
}

export async function joinPublicTurma(turmaId: string, name: string) {
  return apiRequest<any>(`/alunos/public/${turmaId}`, {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
}

// ==================== PROVAS ====================
export async function getProvas() {
  return apiRequest<Prova[]>('/provas');
}

export async function getProvaById(id: string) {
  return apiRequest<Prova>(`/provas/${id}`);
}

export async function getProvasByTurma(turmaId: string) {
  return apiRequest<Prova[]>(`/provas/turma/${turmaId}`);
}

export async function createProva(prova: any, questions: any[]) {
  return apiRequest<Prova>('/provas', {
    method: 'POST',
    body: JSON.stringify({ ...prova, questions }),
  });
}

export async function updateProva(id: string, updates: any) {
  return apiRequest<Prova>(`/provas/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

export async function updateQuestions(provaId: string, questions: any[]) {
  return apiRequest<any>(`/provas/${provaId}/questions`, {
    method: 'PUT',
    body: JSON.stringify({ questions }),
  });
}

export async function deleteProva(id: string) {
  return apiRequest<{ success: boolean }>(`/provas/${id}`, { method: 'DELETE' });
}

// ==================== CORREÇÕES ====================
export async function getCorrecoesByProva(provaId: string) {
  return apiRequest<Correcao[]>(`/correcoes/prova/${provaId}`);
}

export async function getCorrecoesByAluno(alunoId: string) {
  return apiRequest<Correcao[]>(`/correcoes/aluno/${alunoId}`);
}

export async function getCorrecoesByTurma(turmaId: string) {
  return apiRequest<Correcao[]>(`/correcoes/turma/${turmaId}`);
}

export async function getCorrecaoById(id: string) {
  return apiRequest<Correcao>(`/correcoes/${id}`);
}

export async function createCorrecao(data: any) {
  return apiRequest<Correcao>('/correcoes', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function deleteCorrecao(id: string) {
  return apiRequest<{ success: boolean }>(`/correcoes/${id}`, { method: 'DELETE' });
}

export async function processScanCamera(imageBase64: string, provaId: string, totalQuestions: number) {
  return apiRequest<any>('/correcoes/scan', {
    method: 'POST',
    body: JSON.stringify({ image_base64: imageBase64, prova_id: provaId, total_questions: totalQuestions }),
  });
}

// ==================== DASHBOARD ====================
export async function getDashboardStats() {
  return apiRequest<any>('/dashboard/stats');
}

// ==================== LESSON PLANS ====================
export async function getLessonPlans(filters?: any) {
  const params = filters ? new URLSearchParams(filters) : null;
  const qs = params?.toString();
  return apiRequest<LessonPlan[]>(`/lesson-plans${qs ? `?${qs}` : ''}`);
}

export async function getLessonPlanById(id: string) {
  return apiRequest<LessonPlan>(`/lesson-plans/${id}`);
}

export async function getLessonPlanStats() {
  return apiRequest<any>('/lesson-plans/stats');
}

export async function createLessonPlan(plan: Partial<LessonPlan>) {
  return apiRequest<LessonPlan>('/lesson-plans', {
    method: 'POST',
    body: JSON.stringify(plan),
  });
}

export async function updateLessonPlan(id: string, updates: Partial<LessonPlan>) {
  return apiRequest<LessonPlan>(`/lesson-plans/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

export async function deleteLessonPlan(id: string) {
  return apiRequest<{ success: boolean }>(`/lesson-plans/${id}`, { method: 'DELETE' });
}

export async function duplicateLessonPlan(id: string) {
  return apiRequest<LessonPlan>(`/lesson-plans/${id}/duplicate`, { method: 'POST' });
}

export async function getLessonPlansByTurma(turmaId: string) {
  return apiRequest<LessonPlan[]>(`/lesson-plans?turma_id=${turmaId}`);
}

export async function getLessonPlansForExam(turmaId: string) {
  return apiRequest<LessonPlan[]>(`/lesson-plans?turma_id=${turmaId}`);
}

export async function markLessonPlanAsUsed(planId: string, turmaId: string, metadata?: any) {
  return apiRequest<LessonPlan>(`/lesson-plans/${planId}/mark-used`, {
    method: 'POST',
    body: JSON.stringify({ turma_id: turmaId, ...metadata }),
  });
}

export async function getBNCCReferences(educationLevel?: string, subjectArea?: string, component?: string) {
  const params = new URLSearchParams();
  if (educationLevel) params.set('education_level', educationLevel);
  if (subjectArea) params.set('subject_area', subjectArea);
  if (component) params.set('component', component);
  const qs = params.toString();
  return apiRequest<any[]>(`/lesson-plans/bncc${qs ? `?${qs}` : ''}`);
}

export async function searchBNCCReferences(query: string) {
  return apiRequest<any[]>(`/lesson-plans/bncc/search?q=${encodeURIComponent(query)}`);
}

export async function regenerateLessonPlanSection(planId: string, section: string) {
  return apiRequest<{ plan: LessonPlan; section: string; newValue: any }>(`/lesson-plans/${planId}/regenerate-section`, {
    method: 'POST',
    body: JSON.stringify({ section }),
  });
}

export async function generateLessonPlanWithAI(request: LessonPlanGenerationRequest) {
  return apiRequest<Partial<LessonPlan>>('/lesson-plans/generate-ai', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

// ==================== LLM ====================
export async function callLLM(prompt: string, options?: any) {
  return apiRequest<any>('/llm/generate', {
    method: 'POST',
    body: JSON.stringify({ prompt, ...options }),
  });
}

export async function generateQuestions(
  content: string,
  questionTypes: any[],
  lessonPlanIds?: string[],
  userId?: string,
  difficulty_distribution?: { easy: number; medium: number; hard: number }
) {
  return apiRequest<any>('/llm/generate-questions', {
    method: 'POST',
    body: JSON.stringify({ content, questionTypes, lessonPlanIds, userId, difficulty_distribution }),
  });
}

export async function getLLMProviders() {
  return apiRequest<any[]>('/llm/providers');
}

export async function getLLMUsageStats() {
  return apiRequest<any>('/llm/usage');
}

// ==================== MERCADOPAGO ====================
export async function getUserSubscription(_userId?: string) {
  return apiRequest<any>('/mercadopago/subscription');
}

export async function getUserPaymentHistory(_userId?: string, limit?: number) {
  return apiRequest<any[]>(`/mercadopago/payments${limit ? `?limit=${limit}` : ''}`);
}

export async function createSubscriptionCheckout(
  userId: string,
  planId: string,
  planName: string,
  amount: number,
  payerEmail: string,
  backUrl?: string
) {
  return apiRequest<{ init_point: string; subscription_id: string }>('/mercadopago/checkout', {
    method: 'POST',
    body: JSON.stringify({ userId, planId, planName, amount, payerEmail, backUrl }),
  });
}

export async function cancelSubscription(_userId?: string, reason?: string) {
  return apiRequest<{ success: boolean; end_date: string | null }>('/mercadopago/cancel', {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export async function getCardInfo() {
  return apiRequest<{ last_four: string; brand: string; payment_method: string } | null>('/mercadopago/card-info');
}

export async function getFinancialDashboard() {
  return apiRequest<any>('/mercadopago/financial');
}

export async function createPublicSubscriptionCheckout(data: {
  name: string;
  email: string;
  planId: string;
  planName: string;
  amount: number;
  backUrl: string;
}) {
  const response = await fetch(`${API_BASE_URL}/mercadopago/checkout-public`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'Erro ao criar checkout público.');
  }
  return response.json();
}

// ==================== PUBLIC (no auth) ====================
export async function getPublicPlans() {
  const response = await fetch(`${API_BASE_URL}/public/plans`);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'Erro ao carregar planos.');
  }
  return response.json();
}

export async function registerWithPlan(data: {
  name: string;
  email: string;
  planId: string;
  backUrl?: string;
}) {
  const response = await fetch(`${API_BASE_URL}/public/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'Erro ao criar conta.');
  }
  return response.json();
}

export async function registerWithConfirmation(data: {
  name: string;
  email: string;
  planId: string;
  backUrl?: string;
}) {
  const response = await fetch(`${API_BASE_URL}/public/register-confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'Erro ao solicitar confirmação de e-mail.');
  }
  return response.json();
}

// ==================== ADMIN ====================
export async function getSystemStats() {
  return apiRequest<any>('/admin/stats');
}

export async function getAllUsers() {
  return apiRequest<any[]>('/admin/users');
}

export async function updateUser(userId: string, updates: any) {
  return apiRequest<any>(`/admin/users/${userId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

export async function getAppSettings() {
  return apiRequest<any>('/admin/settings');
}

export async function updateAppSettings(settings: any) {
  return apiRequest<any>('/admin/settings', {
    method: 'PUT',
    body: JSON.stringify(settings),
  });
}

export async function updateAppSetting(key: string, value: any) {
  return apiRequest<any>(`/admin/settings/${key}`, {
    method: 'PUT',
    body: JSON.stringify({ value }),
  });
}

export async function getActivityLogs(limit?: number) {
  return apiRequest<any[]>(`/admin/logs${limit ? `?limit=${limit}` : ''}`);
}

export async function getSubscriptionPlans() {
  return apiRequest<any[]>('/admin/plans');
}

export async function updateSubscriptionPlan(id: string, updates: any) {
  return apiRequest<any>(`/admin/plans/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

// ==================== PAGAMENTOS (ADMIN) ====================
export async function getPayments(filters?: any) {
  const params = filters ? new URLSearchParams(filters) : null;
  const qs = params?.toString();
  return apiRequest<any[]>(`/admin/payments${qs ? `?${qs}` : ''}`);
}

export async function getFinancialStats() {
  return apiRequest<any>('/admin/financial-stats');
}

export async function getInvoices(filters?: any) {
  const params = filters ? new URLSearchParams(filters) : null;
  const qs = params?.toString();
  return apiRequest<any[]>(`/admin/invoices${qs ? `?${qs}` : ''}`);
}

export async function getCoupons() {
  return apiRequest<any[]>('/admin/coupons');
}

export async function updatePaymentStatus(paymentId: string, status: string) {
  return apiRequest<any>(`/admin/payments/${paymentId}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  });
}

// ==================== API WRAPPER ====================
export const api = {
  get: <T>(endpoint: string) => apiRequest<T>(endpoint),
  post: <T>(endpoint: string, data?: any) => apiRequest<T>(endpoint, {
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined,
  }),
  put: <T>(endpoint: string, data?: any) => apiRequest<T>(endpoint, {
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined,
  }),
  delete: <T>(endpoint: string) => apiRequest<T>(endpoint, {
    method: 'DELETE',
  }),
};
